import {
  useState,
  useEffect,
  useCallback,
  useMemo } from "react";
import {
  Container,
  FormControl,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useMediaQuery
} from "@mui/material";
import Autocomplete from '../components/SearchableAutocomplete';
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import TextField from '../components/SearchableTextField';
import SidebarLayout from "../components/sidebarLayout";
import { getAllTavoli } from "../APIs/TablesAPI";
import { getAllStanze } from "../APIs/RoomsAPI";
import { getAllClienti, creaCliente, getAllCompanies, createCompany } from "../APIs/ClientsAPI";
import { creaPrenotazione, getAllPrenotazioni, aggiornaPrenotazione, aggiornaStatoPrenotazione } from "../APIs/ReservationsAPI";
import { getAllPackages } from "../APIs/PackagesAPI";
import { getHeliteCheckedInGuests } from "../APIs/HeliteAPI";
import { useNavigate } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import TimePicker24Field from "../components/TimePicker24Field";
import { format, parse, startOfWeek, getDay } from "date-fns";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { it, enUS, sq } from "date-fns/locale";
import { SetupWSEventListener } from "../utils/setupWSEventListener";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import { useSnackbar } from "../contexts/SnackBarContext";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EventReservationPanel from "../components/EventReservationPanel";
import Preventivi from "./Preventivi";
import CongressReservationsPage from "./CongressReservationsPage";
import DateTimeLocal24Field from "../components/DateTimeLocal24Field";
import { localizedErrorMessage } from "../utils/italianErrorMessage";
import { getUnavailableReservationTableIds } from "../utils/reservationAvailability";

const dateFnsLocaleMap = {
  en: { locale: enUS, culture: "en" },
  it: { locale: it, culture: "it" },
  sq: { locale: sq, culture: "sq" },
};

const EMPTY_CLIENTE = {
  name: "", surname: "", email: "", phone: "", roomNumber: "", treatment: "",
};
const formatDateTime24 = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("it-IT", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};
const formatDateDdMmYyyy = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};
const parseTimePickerValue = (value) => {
  if (!value || typeof value !== "string") return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};
const formatTimePickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};
const EMPTY_COMPANY = {
  companyName: "",
  vatNumber: "",
  sdiCode: "",
  address: "",
  legalAddress: "",
  phone: "",
  email: "",
};
const INITIAL_FORM_DATA = {
  clientId: "",
  name: "",
  surname: "",
  eventName: "",
  guests: "",
  bookingDate: "",
  startTime: "",
  endTime: "",
  tableIds: [],
  notes: "",
};
const SLOT_MINUTES = 15;
const DEFAULT_DURATION_MINUTES = 60;
const CALENDAR_MINUTES_START = (8 * 60) + 30;
const CALENDAR_MINUTES_END = (23 * 60) + 30;
const DRAFT_EVENT_ID = "draft-new-reservation";
const RESERVATION_STATUSES = ["PREVENTIVO", "TO_CONFIRM", "CONFIRMED", "COMPLETED", "CANCELLED", "NO_SHOW"];
const ACTIVE_RESERVATION_LOCK_STATUSES = new Set(["TO_CONFIRM", "CONFIRMED"]);
const COMPANY_PLACEHOLDER_SURNAME = "[AZIENDA]";

const RestaurantPrenotazioni = () => {
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const isMobileCalendar = useMediaQuery(theme.breakpoints.down("sm"));
  const calendarDefaultView = isMobileCalendar ? "agenda" : "week";
  const tenantType = localStorage.getItem("tenantType");
  const tenantId = localStorage.getItem("tenantId");
  const { showSnackbar } = useSnackbar();
  const lang = i18n.resolvedLanguage || i18n.language;
  const { culture } = dateFnsLocaleMap[lang] || dateFnsLocaleMap.en;

  const localizer = useMemo(
    () =>
      dateFnsLocalizer({
        format,
        parse,
        startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
        getDay,
        locales: { en: enUS, it, sq },
      }),
    []
  );

  const messages = useMemo(
    () => ({
      date: t("booking.date"),
      time: t("booking.time"),
      event: t("booking.event"),
      today: t("booking.today"),
      week: t("booking.week"),
      day: t("booking.day"),
      next: t("booking.next"),
      previous: t("booking.back"),
      allDay: t("booking.allDay"),
      work_week: t("booking.work_week"),
      month: t("booking.month"),
      tomorrow: t("booking.tomorrow"),
      yesterday: t("booking.yesterday"),
      agenda: t("booking.agenda"),
      noEventsInRange: t("noEventsInRange"),
      showMore: (total) => `+${total} more`,
    }),
    [t]
  );

  const calendarFormats = useMemo(
    () => ({
      timeGutterFormat: "HH:mm",
      eventTimeRangeFormat: ({ start, end }, cultureValue, localizerValue) =>
        `${localizerValue.format(start, "HH:mm", cultureValue)} - ${localizerValue.format(end, "HH:mm", cultureValue)}`,
      agendaTimeRangeFormat: ({ start, end }, cultureValue, localizerValue) =>
        `${localizerValue.format(start, "HH:mm", cultureValue)} - ${localizerValue.format(end, "HH:mm", cultureValue)}`,
    }),
    []
  );

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [tavoli, setTavoli] = useState([]);
  const [roomMap, setRoomMap] = useState({});
  const [clienti, setClienti] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [nuovoEvento, setNuovoEvento] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedReservationCompany, setSelectedReservationCompany] = useState(null);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [selectedTable, setSelectedTable] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [reservationStatusDraft, setReservationStatusDraft] = useState("TO_CONFIRM");
  const [reservationCancellationReason, setReservationCancellationReason] = useState("");
  const [reservationCancellationReasonError, setReservationCancellationReasonError] = useState("");
  const [savingReservationStatus, setSavingReservationStatus] = useState(false);
  const [isEditingPendingReservation, setIsEditingPendingReservation] = useState(false);
  const [reservationEditData, setReservationEditData] = useState({
    guests: "",
    startTime: "",
    endTime: "",
    tableIds: [],
    note: "",
  });
  const [reservationEditError, setReservationEditError] = useState("");
  const [savingReservationEdit, setSavingReservationEdit] = useState(false);
  const [showCancelledOnCalendar, setShowCancelledOnCalendar] = useState(false);
  const [showNoShowOnCalendar, setShowNoShowOnCalendar] = useState(false);
  const [calendarView, setCalendarView] = useState(calendarDefaultView);
  const [eventPanelRefreshToken, setEventPanelRefreshToken] = useState(0);
  const [selectedHeliteGuest, setSelectedHeliteGuest] = useState(null);
  const [helitePickerOpen, setHelitePickerOpen] = useState(false);
  const [heliteGuests, setHeliteGuests] = useState([]);
  const [heliteGuestsLoading, setHeliteGuestsLoading] = useState(false);
  const [heliteSearch, setHeliteSearch] = useState("");
  const calendarViews = isMobileCalendar ? ["day", "agenda"] : ["week", "day", "work_week", "agenda"];
  const effectiveCalendarView = calendarViews.includes(calendarView) ? calendarView : calendarDefaultView;
  const calendarMinWidth = isMobileCalendar && ["week", "work_week"].includes(effectiveCalendarView) ? 720 : 0;
  const timeGutterWidth = isMobileCalendar ? 78 : 86;

  useEffect(() => {
    setCalendarView(calendarDefaultView);
  }, [calendarDefaultView]);

  // â”€â”€ New Client Dialog state
  const [openNewClient, setOpenNewClient]         = useState(false);
  const [newCliente, setNewCliente]               = useState(EMPTY_CLIENTE);
  const [companies, setCompanies]                 = useState([]);
  const [selectedCompany, setSelectedCompany]     = useState(null);
  const [clientFormError, setClientFormError]     = useState("");
  const [clientFormSuccess, setClientFormSuccess] = useState("");
  const [openNewCompany, setOpenNewCompany] = useState(false);
  const [newCompany, setNewCompany] = useState(EMPTY_COMPANY);
  const [newCompanyError, setNewCompanyError] = useState("");
  const [newCompanySuccess, setNewCompanySuccess] = useState("");

  const eventiArray = [...eventi];
  const navigate = useNavigate();

  const formatTableLabel = useCallback(
    (option) => {
      const cleanName = /^\d+ \d+$/.test(option.name)
        ? option.name.replace(/^\d+ /, "")
        : option.name;
      const room = roomMap[option.roomId] ?? option.room;
      const roomName = room?.name ?? "";
      return roomName
        ? `${t("room")} ${roomName} - ${t("table")} ${cleanName}`
        : `${t("table")} ${cleanName}`;
    },
    [roomMap, t]
  );

  const getDisplayClientName = useCallback((client) => {
    if (!client) return "";
    const companyName = client.company?.companyName || "";
    const name = (client.name || "").trim();
    const surname = (client.surname || "").trim();
    if (surname === COMPANY_PLACEHOLDER_SURNAME) {
      return companyName || name || t("reservation");
    }
    return `${name} ${surname}`.trim();
  }, [t]);

  const getReservationStatusLabel = useCallback((status) => {
    if (!status) return "-";
    return t(`reservations_details.status_values.${status}`, { defaultValue: status });
  }, [t]);

  const getReservationStatusChipSx = useCallback((status) => {
    switch (status) {
      case "PREVENTIVO":
        return { backgroundColor: "warning.light", color: "warning.dark", fontWeight: 700 };
      case "TO_CONFIRM":
        return { backgroundColor: "secondary.light", color: "secondary.dark", fontWeight: 700 };
      case "COMPLETED":
        return { backgroundColor: "success.light", color: "success.dark", fontWeight: 700 };
      case "CANCELLED":
        return { backgroundColor: "error.light", color: "error.dark", fontWeight: 700 };
      case "NO_SHOW":
        return { backgroundColor: "warning.light", color: "warning.dark", fontWeight: 700 };
      default:
        return { backgroundColor: "info.light", color: "info.dark", fontWeight: 700 };
    }
  }, []);

  const formatReservationPackages = useCallback((reservation) => {
    const rawPackages = Array.isArray(reservation?.reservationPackages)
      ? reservation.reservationPackages
      : [];
    const packageIds = Array.isArray(reservation?.packageIds) ? reservation.packageIds : [];

    const mappedFromRelations = rawPackages
      .map((entry) => {
        const pkg = entry?.package;
        if (!pkg && Number.isInteger(entry?.packageId)) {
          return {
            id: entry.packageId,
            name: `${t("booking.packageFallbackName")} #${entry.packageId}`,
            priceLabel: null,
          };
        }
        if (!pkg) return null;
        const hasPrice = pkg.price !== null && pkg.price !== undefined && pkg.price !== "";
        return {
          id: pkg.id,
          name: pkg.name || `${t("booking.packageFallbackName")} #${pkg.id}`,
          priceLabel: hasPrice ? `${Number(pkg.price).toFixed(2)} EUR` : null,
        };
      })
      .filter(Boolean);

    if (mappedFromRelations.length > 0) return mappedFromRelations;

    return packageIds
      .filter((id) => Number.isInteger(id) && id > 0)
      .map((id) => ({
        id,
        name: `${t("booking.packageFallbackName")} #${id}`,
        priceLabel: null,
      }));
  }, [t]);

  const formatReservationServices = useCallback((reservation) => {
    const rawServices = Array.isArray(reservation?.eventDay?.services)
      ? reservation.eventDay.services
      : [];

    return rawServices
      .map((entry) => {
        const product = entry?.product;
        if (!product && !Number.isInteger(entry?.productId)) return null;
        const fallbackLabel = t("preventivi.available_services", { defaultValue: "Servizio" });
        const baseName = product?.name || `${fallbackLabel} #${entry.productId}`;
        const variantName = entry?.variant?.name || "";
        const quantity = Number(entry?.quantity || 1);
        return {
          id: entry.id || `${entry.productId}-${entry.variantId || "base"}`,
          name: variantName ? `${baseName} (${variantName})` : baseName,
          quantity,
        };
      })
      .filter(Boolean);
  }, [t]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");

      const [dataTavoli, dataStanze, dataClienti, dataPrenotazioni] = await Promise.all([
        getAllTavoli(token, tenant),
        getAllStanze(token, tenant),
        getAllClienti(token, tenant),
        getAllPrenotazioni(token, tenant, { noCache: true }),
      ]);

      if (Array.isArray(dataStanze)) {
        const map = {};
        dataStanze.forEach((r) => { map[r.id] = r; });
        setRoomMap(map);
      }

      const stanzeTavoli = dataTavoli
        .filter(Boolean)
        .map(({ id, name, maxSeats, roomId, room }) => ({ id, name, maxSeats, roomId, room }));

      const listaClienti = dataClienti.map(({ id, name, surname, company }) => ({
        id, name, surname, company,
      }));

      const prenotazioniEventi = dataPrenotazioni
        .map(({ id, startDate, endDate, guests, client, status, eventName, tableReservations }) => {
          const start = new Date(startDate);
          const end = new Date(endDate);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

          const clientName = client ? getDisplayClientName(client) : t("reservation");
          const mainLabel = (typeof eventName === "string" && eventName.trim().length > 0)
            ? eventName.trim()
            : clientName;

          const tableNames = Array.isArray(tableReservations)
            ? tableReservations.map((tr) => tr?.table?.name).filter(Boolean)
            : [];
          const uniqueTables = [...new Set(tableNames)];
          const tableSuffix = uniqueTables.length
            ? ` - ${t("table")}: ${uniqueTables.join(", ")}`
            : "";

          return {
            id,
            title: `${mainLabel} - ${guests} ${t("guests")}${tableSuffix}`,
            start,
            end,
            allDay: false,
            status: status || "TO_CONFIRM",
          };
        })
        .filter(Boolean);

      const sortedTavoli = stanzeTavoli
        .sort((a, b) => (a.roomId - b.roomId) || (a.id - b.id));

      setTavoli(sortedTavoli);
      setClienti(listaClienti);
      setReservations(dataPrenotazioni);
      setEventi(prenotazioniEventi);
    } catch (error) {
      showSnackbar(t("booking.room_error") + error.message, "error");
    }
  }, [t, showSnackbar, tenantId, getDisplayClientName]);

  const calendarEvents = useMemo(() => {
    const filtered = eventi.filter((eventItem) => {
      const upperStatus = (eventItem?.status || "").toUpperCase();
      if (upperStatus === "CANCELLED" && !showCancelledOnCalendar) return false;
      if (upperStatus === "NO_SHOW" && !showNoShowOnCalendar) return false;
      return true;
    });
    return nuovoEvento ? [...filtered, nuovoEvento] : filtered;
  }, [eventi, nuovoEvento, showCancelledOnCalendar, showNoShowOnCalendar]);

  const createUnavailableTableIds = useMemo(() => {
    if (!formData.bookingDate || !formData.startTime || !formData.endTime) return new Set();
    return getUnavailableReservationTableIds(
      reservations,
      `${formData.bookingDate}T${formData.startTime}`,
      `${formData.bookingDate}T${formData.endTime}`
    );
  }, [formData.bookingDate, formData.endTime, formData.startTime, reservations]);

  const editUnavailableTableIds = useMemo(() => getUnavailableReservationTableIds(
    reservations,
    reservationEditData.startTime,
    reservationEditData.endTime,
    selectedReservation?.id
  ), [reservationEditData.endTime, reservationEditData.startTime, reservations, selectedReservation?.id]);

  const availableCreateTables = useMemo(() => tavoli, [tavoli]);
  const availableEditTables = useMemo(() => tavoli, [tavoli]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const tenant = tenantId || localStorage.getItem("tenantId");
        const raw = await getAllPackages(token, tenant);
        const normalized = Array.isArray(raw)
          ? raw.map((pkg) => ({
              id: pkg.id,
              name: pkg.name || t("booking.packageFallbackName"),
              price: Number(pkg.price || 0),
            }))
          : [];
        setAvailablePackages(normalized);
      } catch {
        setAvailablePackages([]);
      }
    };
    fetchPackages();
  }, [tenantId, t]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const tenant = tenantId || localStorage.getItem("tenantId");
    getAllCompanies(token, tenant)
      .then((data) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => setCompanies([]));
  }, [tenantId]);

  useEffect(() => {
    fetchData();
    const cleanup = SetupWSEventListener(fetchData);
    return cleanup;
  }, [fetchData]);

  const onSelectFunction = (slotInfo) => {
    if (!slotInfo?.start || !slotInfo?.end) return;
    const start = new Date(slotInfo.start);
    const end = new Date(slotInfo.end);
    const startMinutes = (start.getHours() * 60) + start.getMinutes();
    const endMinutes = (end.getHours() * 60) + end.getMinutes();
    const isAllDayLikeSelection = (
      startMinutes === 0
      && endMinutes === 0
      && start.toDateString() === end.toDateString()
    );

    // Ignore all-day/header selections: classic booking must be time-slot based.
    if (isAllDayLikeSelection) return;

    // Ignore anything outside working window shown in the calendar.
    if (startMinutes < CALENDAR_MINUTES_START || startMinutes > CALENDAR_MINUTES_END) return;
    if (endMinutes < CALENDAR_MINUTES_START || endMinutes > CALENDAR_MINUTES_END) return;

    applyTimeSelection(start, end);
  };

  const handleOpenReservationModal = useCallback((calendarEvent) => {
    if (!calendarEvent?.id) return;
    const reservationId = Number(calendarEvent.id);
    const reservation = reservations.find((item) => item.id === reservationId);
    if (!reservation) return;

    const nextStatus = reservation.status || "TO_CONFIRM";
    setSelectedReservation(reservation);
    setReservationStatusDraft(nextStatus);
    setReservationCancellationReason(reservation.cancellationReason || "");
    setReservationCancellationReasonError("");
    setReservationEditData({
      guests: reservation.guests ?? "",
      startTime: formatDateTimeLocal(reservation.startDate),
      endTime: formatDateTimeLocal(reservation.endDate),
      tableIds: (reservation.tableReservations || []).map((tr) => tr.tableId ?? tr.table?.id).filter(Boolean),
      note: reservation.note || "",
    });
    setReservationEditError("");
    setIsEditingPendingReservation(false);
    setReservationModalOpen(true);
  }, [reservations]);

  const handleCloseReservationModal = () => {
    setReservationModalOpen(false);
    setSelectedReservation(null);
    setReservationStatusDraft("TO_CONFIRM");
    setReservationCancellationReason("");
    setReservationCancellationReasonError("");
    setIsEditingPendingReservation(false);
    setReservationEditError("");
    setReservationEditData({
      guests: "",
      startTime: "",
      endTime: "",
      tableIds: [],
      note: "",
    });
    setSavingReservationStatus(false);
  };

  const handleReservationEditField = (field, value) => {
    setReservationEditData((prev) => ({ ...prev, [field]: value }));
    if (reservationEditError) setReservationEditError("");
  };

  const handleReservationEditTablesChange = (_, newValue) => {
    setReservationEditData((prev) => ({
      ...prev,
      tableIds: newValue.map((table) => table.id),
    }));
    if (reservationEditError) setReservationEditError("");
  };

  const selectedReservationEditTables = useMemo(
    () => tavoli.filter((table) => reservationEditData.tableIds.includes(table.id)),
    [tavoli, reservationEditData.tableIds]
  );

  const handleSaveReservationStatus = async () => {
    if (!selectedReservation?.id) return;
    if (selectedReservation.status === "COMPLETED") {
      showSnackbar(t("reservations_details.completed_locked", { defaultValue: "Le prenotazioni completate non possono essere modificate." }), "warning");
      return;
    }

    const reason = reservationCancellationReason.trim();
    if (reservationStatusDraft === "CANCELLED" && !reason) {
      setReservationCancellationReasonError(t("reservations_details.provide_cancellation_reason"));
      return;
    }

    setSavingReservationStatus(true);
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");
      const updated = await aggiornaStatoPrenotazione(
        selectedReservation.id,
        reservationStatusDraft,
        token,
        tenant,
        reservationStatusDraft === "CANCELLED" ? reason : undefined
      );

      setReservations((prev) => prev.map((res) => (
        res.id === selectedReservation.id
          ? {
              ...res,
              status: updated?.status ?? reservationStatusDraft,
              cancellationReason: updated?.cancellationReason ?? null,
            }
          : res
      )));

      setSelectedReservation((prev) => (
        prev
          ? {
              ...prev,
              status: updated?.status ?? reservationStatusDraft,
              cancellationReason: updated?.cancellationReason ?? null,
            }
          : prev
      ));

      showSnackbar(t("save"), "success");
      await fetchData();
      setEventPanelRefreshToken((prev) => prev + 1);
      handleCloseReservationModal();
    } catch (error) {
      showSnackbar(`${t("reservations_details.status_update_error")}: ${error.message}`, "error");
    } finally {
      setSavingReservationStatus(false);
    }
  };

  const handleSaveReservationEdits = async () => {
    if (!selectedReservation?.id) return;

    const guests = parseInt(reservationEditData.guests, 10);
    const start = reservationEditData.startTime ? new Date(reservationEditData.startTime) : null;
    const end = reservationEditData.endTime ? new Date(reservationEditData.endTime) : null;
    const tableIds = reservationEditData.tableIds;

    if (!Number.isInteger(guests) || guests <= 0) {
      setReservationEditError(t("booking.error"));
      return;
    }
    if (!Array.isArray(tableIds) || tableIds.length === 0) {
      setReservationEditError(t("booking.error"));
      return;
    }
    if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime()) || end <= start) {
      setReservationEditError(t("booking.error"));
      return;
    }
    const selectedEditTableIds = new Set(tableIds.map(Number));
    const conflictingTable = tavoli.find((table) => (
      selectedEditTableIds.has(Number(table.id)) && editUnavailableTableIds.has(Number(table.id))
    ));
    if (conflictingTable) {
      setReservationEditError(`${formatTableLabel(conflictingTable)} - PRENOTATO`);
      return;
    }

    setSavingReservationEdit(true);
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");
      const selectedClientId = Number(selectedReservation.clientId ?? selectedReservation.client?.id);
      const payload = {
        ...(Number.isInteger(selectedClientId) && selectedClientId > 0 ? { clientId: selectedClientId } : {}),
        guests,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        tableIds,
        note: reservationEditData.note,
        status: selectedReservation.status || "TO_CONFIRM",
      };

      await aggiornaPrenotazione(selectedReservation.id, payload, token, tenant);
      await fetchData();
      setEventPanelRefreshToken((prev) => prev + 1);
      setReservationEditError("");
      setIsEditingPendingReservation(false);
      showSnackbar(t("reservations_details.save_booking"), "success");
    } catch (error) {
      setReservationEditError(localizedErrorMessage(error, t("reservations_details.save_booking_error")));
    } finally {
      setSavingReservationEdit(false);
    }
  };

  const eventPropGetter = useCallback((event) => {
    const isDraft = Boolean(event?.isDraft);
    let bg = theme.palette.primary.main;
    if (isDraft) bg = theme.palette.warning.main;
    if (!isDraft && event?.status === "TO_CONFIRM") bg = theme.palette.secondary.main;
    if (!isDraft && event?.status === "CANCELLED") bg = theme.palette.error.main;
    if (!isDraft && event?.status === "NO_SHOW") bg = theme.palette.warning.dark;
    if (!isDraft && event?.status === "COMPLETED") bg = theme.palette.success.main;

    return {
      style: {
        backgroundColor: bg,
        color: theme.palette.getContrastText(bg),
        border: "none",
        borderRadius: 10,
        boxShadow: `0 6px 18px ${alpha(bg, 0.35)}`,
        fontWeight: 600,
      },
    };
  }, [theme]);

  const formatDateTimeLocal = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatDateLocal = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const formatTimeLocal = (value) => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const buildDateFromForm = (datePart, timePart) => {
    if (!datePart || !timePart) return null;
    const dateTime = new Date(`${datePart}T${timePart}`);
    if (Number.isNaN(dateTime.getTime())) return null;
    return dateTime;
  };

  const applyTimeSelection = (start, end) => {
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) return;
    if (!(end instanceof Date) || Number.isNaN(end.getTime()) || end <= start) {
      end = new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60 * 1000);
    }

    setFormData((prev) => ({
      ...prev,
      bookingDate: formatDateLocal(start),
      startTime: formatTimeLocal(start),
      endTime: formatTimeLocal(end),
    }));
    setNuovoEvento(() => ({
      id: DRAFT_EVENT_ID,
      isDraft: true,
      title: t("reservation"),
      start,
      end,
    }));
  };

  const handleTimeFieldChange = (field, value) => {
    const nextTime = typeof value === "string" ? value : "";
    if (!nextTime) return;

    const currentDate = formData.bookingDate || formatDateLocal(new Date());
    const currentStart = buildDateFromForm(currentDate, field === "startTime" ? nextTime : formData.startTime);
    const currentEnd = buildDateFromForm(currentDate, field === "endTime" ? nextTime : formData.endTime);

    let nextStart = currentStart;
    let nextEnd = currentEnd;

    if (field === "startTime") {
      if (!nextStart) return;
      if (!nextEnd || nextEnd <= nextStart) {
        nextEnd = new Date(nextStart.getTime() + DEFAULT_DURATION_MINUTES * 60 * 1000);
      }
    } else {
      if (!nextEnd) return;
      if (!nextStart) {
        nextStart = new Date(nextEnd.getTime() - DEFAULT_DURATION_MINUTES * 60 * 1000);
      }
      if (nextEnd <= nextStart) {
        nextEnd = new Date(nextStart.getTime() + SLOT_MINUTES * 60 * 1000);
      }
    }

    applyTimeSelection(nextStart, nextEnd);
  };

  const handleBookingDateChange = (value) => {
    const nextDate = typeof value === "string" ? value : "";
    setFormData((prev) => ({ ...prev, bookingDate: nextDate }));

    const nextStart = buildDateFromForm(nextDate, formData.startTime);
    const nextEnd = buildDateFromForm(nextDate, formData.endTime);
    if (nextStart && nextEnd) {
      applyTimeSelection(nextStart, nextEnd);
    }
  };

  const handleAdjustTime = (field, deltaMinutes) => {
    const current = buildDateFromForm(formData.bookingDate, formData[field]);
    if (!current || Number.isNaN(current.getTime())) return;
    const adjusted = new Date(current.getTime() + deltaMinutes * 60 * 1000);
    handleTimeFieldChange(field, formatTimeLocal(adjusted));
  };

  const handleOpenNewClient = () => {
    setNewCliente(EMPTY_CLIENTE);
    setSelectedCompany(null);
    setClientFormError("");
    setClientFormSuccess("");
    setOpenNewClient(true);
  };

  const handleCloseNewClient = () => {
    setOpenNewClient(false);
  };

  const handleOpenHelitePicker = async () => {
    setHelitePickerOpen(true);

    setHeliteGuestsLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");
      const payload = await getHeliteCheckedInGuests(token, tenant, { activeOnly: true });
      setHeliteGuests(Array.isArray(payload?.guests) ? payload.guests : []);
    } catch {
      setHeliteGuests([]);
    } finally {
      setHeliteGuestsLoading(false);
    }
  };

  const filteredHeliteGuests = useMemo(() => {
    const query = heliteSearch.trim().toLowerCase();
    if (!query) return heliteGuests;
    return heliteGuests.filter((guest) => (
      [
        guest.roomnr,
        guest.holderName,
        guest.number,
        guest.idrispren,
      ].filter(Boolean).join(" ").toLowerCase().includes(query)
    ));
  }, [heliteGuests, heliteSearch]);

  const handleSaveNewClient = async () => {
    // â”€â”€ Only name & surname required
    if (!newCliente.name.trim() || !newCliente.surname.trim()) {
      setClientFormError(t("client_page.error_required_fields"));
      return;
    }

    setClientFormError("");

    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");

      const hasEmail = newCliente.email.trim() !== "";
      const hasPhone = newCliente.phone.trim() !== "";

      const created = await creaCliente(
        {
          name:    newCliente.name,
          surname: newCliente.surname,
          ...(hasEmail              && { email:      newCliente.email }),
          ...(hasPhone              && { phone:      newCliente.phone }),
          ...(newCliente.roomNumber && { roomNumber: newCliente.roomNumber }),
          ...(newCliente.treatment  && { treatment:  newCliente.treatment }),
          ...(selectedCompany?.id   && { companyId:  selectedCompany.id }),
        },
        token,
        tenant
      );

      setClientFormSuccess(t("client_page.success_create"));
      await fetchData();

      const newClientObj = created ?? {
        id: null,
        name: newCliente.name,
        surname: newCliente.surname,
        company: selectedCompany ?? undefined,
      };
      setSelectedClient(newClientObj);
      setFormData((prev) => ({
        ...prev,
        clientId: newClientObj.id ?? "",
        name:     newClientObj.name,
        surname:  newClientObj.surname,
      }));

      setTimeout(() => {
        setClientFormSuccess("");
        setOpenNewClient(false);
      }, 1500);

    } catch (err) {
  const raw = err?.message ?? (typeof err === "string" ? err : "");
  if (raw.toLowerCase().includes("email")) {
    setClientFormError(
      t("client_page_error_duplicate_email")
    );
  } else if (raw.toLowerCase().includes("unique constraint") || raw.toLowerCase().includes("unique")) {
    setClientFormError(
      t("client_page_error_create")
    );
  } else {
    setClientFormError(raw || t("client_page_error_create"));
  }
}
  };

  const handleOpenNewCompany = () => {
    setNewCompany(EMPTY_COMPANY);
    setNewCompanyError("");
    setNewCompanySuccess("");
    setOpenNewCompany(true);
  };

  const handleCloseNewCompany = () => {
    setOpenNewCompany(false);
  };

  const handleSaveNewCompany = async () => {
    if (!newCompany.companyName.trim()) {
      setNewCompanyError(t("bookingExtra.companyNameRequired"));
      return;
    }

    setNewCompanyError("");
    try {
      const token = localStorage.getItem("accessToken");
      const tenant = tenantId || localStorage.getItem("tenantId");
      const created = await createCompany(
        {
          companyName: newCompany.companyName.trim(),
          ...(newCompany.vatNumber.trim() && { vatNumber: newCompany.vatNumber.trim() }),
          ...(newCompany.sdiCode.trim() && { sdiCode: newCompany.sdiCode.trim() }),
          ...(newCompany.address.trim() && { address: newCompany.address.trim() }),
          ...(newCompany.legalAddress.trim() && { legalAddress: newCompany.legalAddress.trim() }),
          ...(newCompany.phone.trim() && { phone: newCompany.phone.trim() }),
          ...(newCompany.email.trim() && { email: newCompany.email.trim() }),
        },
        token,
        tenant
      );

      const updatedCompanies = await getAllCompanies(token, tenant);
      const nextCompanies = Array.isArray(updatedCompanies) ? updatedCompanies : [];
      setCompanies(nextCompanies);

      const selected = created?.id
        ? nextCompanies.find((company) => company.id === created.id) || created
        : created;
      setSelectedReservationCompany(selected || null);
      setSelectedClient(null);
      setFormData((prev) => ({
        ...prev,
        clientId: "",
        name: "",
        surname: "",
      }));

      setNewCompanySuccess(t("bookingExtra.companyCreated"));
      setTimeout(() => {
        setNewCompanySuccess("");
        setOpenNewCompany(false);
      }, 1200);
    } catch (err) {
      const raw = err?.message ?? (typeof err === "string" ? err : "");
      setNewCompanyError(raw || "Errore creazione azienda.");
    }
  };

  const checkForms = async () => {
    if (!selectedClient && !selectedReservationCompany && !selectedHeliteGuest) return t("heliteGuestPicker.reservationRecipientRequired");
    if (!selectedGuest)  return t("heliteGuestPicker.reservationGuestsRequired");
    if (!formData.bookingDate || !formData.startTime || !formData.endTime) return t("heliteGuestPicker.reservationDateTimeRequired");
    if (selectedTable.length === 0) return t("heliteGuestPicker.reservationTableRequired");

    const newStart = buildDateFromForm(formData.bookingDate, formData.startTime);
    const newEnd   = buildDateFromForm(formData.bookingDate, formData.endTime);
    if (!newStart || !newEnd || newEnd <= newStart) return t("heliteGuestPicker.reservationInvalidTimeRange");
    if (newStart.getTime() < Date.now() || newEnd.getTime() < Date.now()) {
      return t("heliteGuestPicker.reservationPastDate");
    }

    const selectedTableIds = new Set(selectedTable.map((tbl) => tbl.id));

    for (const res of reservations) {
      const existingStatus = res.status || "TO_CONFIRM";
      if (!ACTIVE_RESERVATION_LOCK_STATUSES.has(existingStatus)) continue;
      const existingStart = new Date(res.startDate);
      const existingEnd   = new Date(res.endDate);
      const overlaps = newStart < existingEnd && newEnd > existingStart;
      if (!overlaps) continue;

      const conflictingTr = res.tableReservations?.find((tr) =>
        selectedTableIds.has(tr.tableId ?? tr.table?.id)
      );

      if (conflictingTr) {
        const label = formatTableLabel({
          name:   conflictingTr.table?.name   ?? "",
          roomId: conflictingTr.table?.roomId ?? null,
          room:   conflictingTr.table?.room   ?? null,
        });
        return `${label} ${t("booking.already_booked_overlap") || "e gia prenotato in questo orario"}`;
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");
    const tenant = tenantId || localStorage.getItem("tenantId");

    const packagesNote = selectedPackages.length
      ? `\n\n[${t("booking.packagesBookedNoteTitle")}]\n- ${selectedPackages
          .map((pkg) => `${pkg.name}${pkg.price ? ` (${pkg.price.toFixed(2)} EUR)` : ""}`)
          .join("\n- ")}`
      : "";
    const mergedNote = `${formData.notes || ""}${packagesNote}`.trim();

    const selectedPackageIds = selectedPackages
      .map((pkg) => pkg?.id)
      .filter((id) => Number.isInteger(id) && id > 0);

    const parsedClientId = parseInt(formData.clientId, 10);
    const hasClientId = Number.isInteger(parsedClientId) && parsedClientId > 0;

    const prenotazione = {
      tableIds:  formData.tableIds,
      guests:    parseInt(formData.guests, 10),
      startTime: buildDateFromForm(formData.bookingDate, formData.startTime)?.toISOString(),
      endTime:   buildDateFromForm(formData.bookingDate, formData.endTime)?.toISOString(),
      note:      mergedNote,
      ...(formData.eventName?.trim() ? { eventName: formData.eventName.trim() } : {}),
      ...(hasClientId ? { clientId: parsedClientId } : {}),
      ...(!hasClientId && selectedReservationCompany?.id ? { companyId: Number(selectedReservationCompany.id) } : {}),
      ...(selectedHeliteGuest ? { heliteGuest: selectedHeliteGuest } : {}),
      ...(selectedPackageIds.length > 0 ? { packageIds: selectedPackageIds } : {}),
    };

    try {
      const err = await checkForms();
      if (err) throw err;
      await creaPrenotazione(prenotazione, token, tenant);
      eventiArray.push(nuovoEvento);
      setEventi([...eventiArray]);
      navigate("/lista_prenotazioni");
    } catch (error) {
      showSnackbar(t("booking.error") + (error?.message || error || "Errore sconosciuto"), "error");
    }
  };

  const handleCancel = () => {
    setFormData(INITIAL_FORM_DATA);
    setSelectedClient(null);
    setSelectedReservationCompany(null);
    setSelectedGuest(null);
    setSelectedHeliteGuest(null);
    setSelectedTable([]);
    setSelectedPackages([]);
    setNuovoEvento(null);
  };

  const opt = `(${t("optional")})`;
  const isSelectedReservationCompleted = selectedReservation?.status === "COMPLETED";

  return (
    <SidebarLayout className="admin-responsive-page">
      <Container sx={{ mt: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {t("reservation")}
        </Typography>

        {formData.bookingDate && formData.startTime && (
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {`${t("selected")}: ${formData.bookingDate} ${formData.startTime} - ${formData.endTime || "--:--"}`}
          </Typography>
        )}

        <Paper
          className="rbc_prenotazioni_wrapper"
          sx={{
            p: { xs: 1, md: 2 },
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${theme.palette.background.paper} 55%)`,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary", fontWeight: 600 }}>
            {t("booking.calendar_help")}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 1.25, flexWrap: "wrap" }}>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={showCancelledOnCalendar}
                  onChange={(e) => setShowCancelledOnCalendar(e.target.checked)}
                />
              )}
              label={t("bookingExtra.showCancelled")}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={showNoShowOnCalendar}
                  onChange={(e) => setShowNoShowOnCalendar(e.target.checked)}
                />
              )}
              label={t("bookingExtra.showNoShow")}
            />
          </Stack>
          <Box sx={{
            maxWidth: "100%",
            overflowX: calendarView === "agenda" ? "hidden" : "auto",
            WebkitOverflowScrolling: "touch",
            "& .rbc-time-content": { touchAction: "pan-y" },
            "& .rbc-month-view":   { touchAction: "pan-y" },
            "& .rbc-time-view":    { touchAction: "pan-y" },
            "& .rbc-time-view .rbc-time-gutter .rbc-timeslot-group": { minHeight: 56 },
            "& .rbc-timeslot-group": { minHeight: 56 },
            "& .rbc-time-view .rbc-time-gutter, & .rbc-time-view .rbc-time-header-gutter": {
              flex: `0 0 ${timeGutterWidth}px`,
              width: timeGutterWidth,
              minWidth: timeGutterWidth,
              maxWidth: timeGutterWidth,
              backgroundColor: theme.palette.background.paper,
            },
            "& .rbc-time-view .rbc-time-gutter": {
              position: { xs: "sticky", sm: "static" },
              left: 0,
              zIndex: 3,
              borderRight: `1px solid ${alpha(theme.palette.text.primary, 0.14)}`,
              boxShadow: { xs: `6px 0 10px ${alpha(theme.palette.common.black, 0.08)}`, sm: "none" },
            },
            "& .rbc-time-view .rbc-label": {
              display: "block",
              width: "100%",
              padding: { xs: "0 8px 0 6px", sm: "0 10px" },
              color: theme.palette.text.primary,
              fontWeight: 800,
              fontSize: { xs: "0.78rem", sm: "0.82rem" },
              lineHeight: "1.2",
              textAlign: "right",
              whiteSpace: "nowrap",
            },
            "& .rbc-time-view .rbc-day-slot .rbc-time-slot": {
              borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            },
            "& .rbc-time-view .rbc-day-slot .rbc-timeslot-group": {
              backgroundImage: `linear-gradient(to bottom, transparent calc(50% - 1px), ${alpha(theme.palette.text.primary, 0.08)} calc(50% - 1px), ${alpha(theme.palette.text.primary, 0.08)} 50%, transparent 50%)`,
            },
            "& .rbc-calendar": { minWidth: calendarMinWidth, width: "100%" },
            "& .rbc-toolbar": {
              gap: 1,
              flexWrap: "wrap",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "center",
            },
            "& .rbc-toolbar-label": {
              minWidth: 0,
              width: { xs: "100%", sm: "auto" },
              order: { xs: -1, sm: 0 },
              textAlign: "center",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              whiteSpace: "normal",
            },
            "& .rbc-btn-group": {
              width: { xs: "100%", sm: "auto" },
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              whiteSpace: "normal",
            },
            "& .rbc-btn-group button": {
              flex: { xs: "1 1 auto", sm: "0 0 auto" },
              minWidth: { xs: 0, sm: "auto" },
              padding: { xs: "6px 8px", sm: "6px 12px" },
              fontSize: { xs: "0.78rem", sm: "0.875rem" },
            },
            "& .rbc-agenda-view": { overflow: "hidden" },
            "& .rbc-agenda-view table.rbc-agenda-table": { width: "100%", tableLayout: { xs: "fixed", sm: "auto" } },
            "& .rbc-agenda-view .rbc-agenda-date-cell": { width: { xs: 78, sm: "auto" } },
            "& .rbc-agenda-view .rbc-agenda-time-cell": { width: { xs: 100, sm: "auto" } },
            "& .rbc-agenda-view .rbc-agenda-event-cell": {
              whiteSpace: { xs: "normal", sm: "nowrap" },
              overflowWrap: "anywhere",
            },
          }}>
            <Calendar
              min={new Date(0, 0, 0, 8, 30, 0)}
              max={new Date(0, 0, 0, 23, 30, 0)}
              className="rbc_prenotazioni"
              localizer={localizer}
              messages={messages}
              formats={calendarFormats}
              culture={culture}
              events={calendarEvents}
              eventPropGetter={eventPropGetter}
              startAccessor="start"
              endAccessor="end"
              style={{ height: "clamp(560px, 72vh, 900px)" }}
              selectable="ignoreEvents"
              longPressThreshold={10}
              onSelectSlot={(slotInfo) => {
                onSelectFunction(slotInfo);
              }}
              onSelectEvent={handleOpenReservationModal}
              popup
              allDaySlot={false}
              showMultiDayTimes
              views={calendarViews}
              view={effectiveCalendarView}
              onView={setCalendarView}
              defaultView={calendarDefaultView}
              step={SLOT_MINUTES}
              timeslots={60 / SLOT_MINUTES}
            />
          </Box>
        </Paper>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>{t("bookingExtra.classicReservation")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexDirection: { xs: "column", sm: "row" } }}>
            <Autocomplete
              fullWidth
              options={clienti}
              value={selectedClient}
              getOptionLabel={(option) =>
                option.company?.companyName
                  ? `${getDisplayClientName(option)} - ${option.company.companyName}`
                  : `${getDisplayClientName(option)}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, newValue) => {
                setSelectedClient(newValue);
                setSelectedReservationCompany(newValue?.company ?? null);
                setFormData((prev) => ({
                  ...prev,
                  clientId: newValue?.id ?? "",
                  name:     newValue?.name ?? "",
                  surname:  newValue?.surname ?? "",
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label={t("booking.name")} variant="outlined" />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenNewClient}
              sx={{ whiteSpace: "nowrap", height: 56, width: { xs: "100%", sm: "auto" } }}
            >
              {t("booking_newClient")}
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexDirection: { xs: "column", sm: "row" } }}>
            <Autocomplete
              fullWidth
              options={companies}
              value={selectedReservationCompany}
              getOptionLabel={(o) => `${o.companyName}${o.vatNumber ? ` (${o.vatNumber})` : ""}`}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              noOptionsText={t("client_page.company_no_options")}
              onChange={(_, value) => {
                setSelectedReservationCompany(value);
                if (value) {
                  setSelectedClient(null);
                  setFormData((prev) => ({
                    ...prev,
                    clientId: "",
                    name: "",
                    surname: "",
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("bookingExtra.companyAlternative")}
                  helperText={t("bookingExtra.companyHelper")}
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={handleOpenNewCompany}
              sx={{ whiteSpace: "nowrap", height: 56, width: { xs: "100%", sm: "auto" } }}
            >
              {t("bookingExtra.newCompanyUpper")}
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: { xs: "stretch", sm: "center" }, flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" } }}>
            {selectedHeliteGuest ? (
              <Chip
                label={`${t("heliteGuestPicker.roomWithNumber", { room: selectedHeliteGuest.roomnr || "-" })} - ${selectedHeliteGuest.holderName || "-"}`}
                onDelete={() => setSelectedHeliteGuest(null)}
                sx={{ maxWidth: { xs: "100%", sm: "calc(100% - 150px)" }, "& .MuiChip-label": { overflowWrap: "anywhere", whiteSpace: "normal" } }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                {t("heliteGuestPicker.noLinkedGuest")}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenHelitePicker}
              sx={{ whiteSpace: "nowrap", height: 40, width: { xs: "100%", sm: "auto" } }}
            >
              {t("heliteGuestPicker.selectButton")}
            </Button>
          </Box>
          <TextField
            fullWidth
            label={t("bookingExtra.eventNameOptional")}
            value={formData.eventName}
            onChange={(e) => setFormData((prev) => ({ ...prev, eventName: e.target.value }))}
            helperText={t("bookingExtra.eventNameHint")}
          />

          <TextField
            label={getTenantTranslation(t, tenantType, "bookingGuests")}
            name="guests"
            type="number"
            value={formData.guests}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, guests: e.target.value }));
              setSelectedGuest(Number(e.target.value) || null);
            }}
            inputProps={{ min: 1 }}
          />

          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={availableCreateTables}
              value={selectedTable}
              getOptionLabel={(option) =>
                `${formatTableLabel(option)} (${option.maxSeats} ${getTenantTranslation(t, tenantType, "seats")})`
                + `${createUnavailableTableIds.has(Number(option.id)) ? " - PRENOTATO" : ""}`
              }
              getOptionDisabled={(option) => createUnavailableTableIds.has(Number(option.id))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterSelectedOptions
              onChange={(e, newValue) => {
                setSelectedTable(newValue);
                setFormData((prev) => ({ ...prev, tableIds: newValue.map((tbl) => tbl.id) }));
              }}
              renderInput={(params) => (
                <TextField {...params} label={getTenantTranslation(t, tenantType, "bookingTable")} />
              )}
            />
          </FormControl>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
              {t("booking.time")}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t("booking.date")}
                  type="date"
                  value={formData.bookingDate}
                  onChange={(e) => handleBookingDateChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TimePicker24Field
                  label={t("fromTime")}
                  value={parseTimePickerValue(formData.startTime)}
                  onChange={(value) => handleTimeFieldChange("startTime", formatTimePickerValue(value))}
                  ampm={false}
                  inputFormat="HH:mm"
                  mask="__:__"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ ...params.inputProps, readOnly: true, autoComplete: "off" }}
                    />
                  )}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => handleAdjustTime("startTime", -SLOT_MINUTES)}>
                    -{SLOT_MINUTES}m
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleAdjustTime("startTime", SLOT_MINUTES)}>
                    +{SLOT_MINUTES}m
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <TimePicker24Field
                  label={t("toTime")}
                  value={parseTimePickerValue(formData.endTime)}
                  onChange={(value) => handleTimeFieldChange("endTime", formatTimePickerValue(value))}
                  ampm={false}
                  inputFormat="HH:mm"
                  mask="__:__"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ ...params.inputProps, readOnly: true, autoComplete: "off" }}
                    />
                  )}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => handleAdjustTime("endTime", -SLOT_MINUTES)}>
                    -{SLOT_MINUTES}m
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleAdjustTime("endTime", SLOT_MINUTES)}>
                    +{SLOT_MINUTES}m
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
              {t("booking.packagesLabel")}
            </Typography>
            <Autocomplete
              multiple
              options={availablePackages}
              value={selectedPackages}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, newValue) => setSelectedPackages(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t("booking.packagesSelect")} />
              )}
            />
            {selectedPackages.length > 0 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                {selectedPackages.map((pkg) => (
                  <Chip
                    key={pkg.id}
                    label={`${pkg.name}${pkg.price ? ` - ${pkg.price.toFixed(2)} EUR` : ""}`}
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Paper>

          <TextField
            label={t("booking.notes")}
            name="notes"
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            multiline
            rows={3}
          />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexDirection: { xs: "column-reverse", sm: "row" } }}>
            <Button variant="outlined" type="button" onClick={handleCancel}>
              {t("cancel_button")}
            </Button>
            <Button variant="contained" type="submit">
              {t("booking.submit")}
            </Button>
          </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>{t("bookingExtra.multiEventQuote")}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <EventReservationPanel
              t={t}
              showSnackbar={showSnackbar}
              onDataChanged={fetchData}
              refreshToken={eventPanelRefreshToken}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>{t("bookingExtra.quotes")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Preventivi
              embedded
              onAfterSendQuote={async () => {
                await fetchData();
                setEventPanelRefreshToken((prev) => prev + 1);
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Container>

      {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
          NEW CLIENT DIALOG
          â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
      <Dialog open={openNewClient} onClose={handleCloseNewClient} maxWidth="sm" fullWidth>
        <DialogTitle>{t("client_page.add_client")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.name")} *`}
                value={newCliente.name}
                onChange={(e) => setNewCliente({ ...newCliente, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.surname")} *`}
                value={newCliente.surname}
                onChange={(e) => setNewCliente({ ...newCliente, surname: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.email")} ${opt}`}
                value={newCliente.email}
                onChange={(e) => setNewCliente({ ...newCliente, email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.phone")} ${opt}`}
                value={newCliente.phone}
                onChange={(e) => setNewCliente({ ...newCliente, phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.room")} ${opt}`}
                value={newCliente.roomNumber}
                onChange={(e) => setNewCliente({ ...newCliente, roomNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={`${t("client_page.treatment")} ${opt}`}
                value={newCliente.treatment}
                onChange={(e) => setNewCliente({ ...newCliente, treatment: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={companies}
                getOptionLabel={(o) => `${o.companyName}${o.vatNumber ? ` (${o.vatNumber})` : ""}`}
                value={selectedCompany}
                onChange={(_, val) => setSelectedCompany(val)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                noOptionsText={t("client_page.company_no_options")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t("client_page.company_name")} ${opt}`}
                    helperText={
                      companies.length === 0
                        ? t("client_page.company_hint")
                        : t("client_page.company_search_hint")
                    }
                  />
                )}
              />
            </Grid>

            {clientFormError   && <Grid item xs={12}><Alert severity="error">{clientFormError}</Alert></Grid>}
            {clientFormSuccess && <Grid item xs={12}><Alert severity="success">{clientFormSuccess}</Alert></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewClient}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleSaveNewClient}>
            {t("client_page.add_client")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openNewCompany} onClose={handleCloseNewCompany} maxWidth="sm" fullWidth>
        <DialogTitle>{t("bookingExtra.newCompany")}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("bookingExtra.companyName")}
                value={newCompany.companyName}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, companyName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.vatOptional")}
                value={newCompany.vatNumber}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, vatNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.sdiOptional")}
                value={newCompany.sdiCode}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, sdiCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.emailOptional")}
                value={newCompany.email}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.phoneOptional")}
                value={newCompany.phone}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.addressOptional")}
                value={newCompany.address}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("bookingExtra.legalAddressOptional")}
                value={newCompany.legalAddress}
                onChange={(e) => setNewCompany((prev) => ({ ...prev, legalAddress: e.target.value }))}
              />
            </Grid>

            {newCompanyError && <Grid item xs={12}><Alert severity="error">{newCompanyError}</Alert></Grid>}
            {newCompanySuccess && <Grid item xs={12}><Alert severity="success">{newCompanySuccess}</Alert></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewCompany}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleSaveNewCompany}>
            {t("bookingExtra.createCompany")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reservationModalOpen} onClose={handleCloseReservationModal} fullWidth maxWidth="md">
        <DialogTitle>{t("reservations_details.title")}</DialogTitle>
        <DialogContent dividers>
          {selectedReservation && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {!!selectedReservation.eventName && (
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("bookingExtra.event")} {selectedReservation.eventName}
                </Typography>
              )}
              <Typography variant="h6">
                {getDisplayClientName(selectedReservation.client)}
              </Typography>
              {selectedReservation.client?.email && (
                <Typography color="text.secondary">{selectedReservation.client.email}</Typography>
              )}
              <Typography>
                {selectedReservation.tableReservations?.map((tr) => formatTableLabel({
                  name: tr.table?.name ?? "",
                  roomId: tr.table?.roomId ?? null,
                  room: tr.table?.room ?? null,
                })).join(", ")}
              </Typography>
              <Typography>
                {t("reservations_details.date")}: {formatDateTime24(selectedReservation.startDate)} - {formatDateTime24(selectedReservation.endDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("reservations_details.createdAt")}: {formatDateTime24(selectedReservation.createdAt)}
              </Typography>
              {selectedReservation.guests !== null && selectedReservation.guests !== undefined && (
                <Typography variant="body2">
                  {t("guests")}: {selectedReservation.guests}
                </Typography>
              )}
              {!!selectedReservation.note && (
                <Typography variant="body2">
                  {t("booking.notes")}: {selectedReservation.note}
                </Typography>
              )}
              {formatReservationPackages(selectedReservation).length > 0 && (
                <Typography variant="body2">
                  {t("booking.packagesLabel")}: {formatReservationPackages(selectedReservation)
                    .map((pkg) => `${pkg.name}${pkg.priceLabel ? ` (${pkg.priceLabel})` : ""}`)
                    .join(", ")}
                </Typography>
              )}
              {formatReservationServices(selectedReservation).length > 0 && (
                <Typography variant="body2">
                  {t("preventivi.available_services", { defaultValue: "Servizi" })}: {formatReservationServices(selectedReservation)
                    .map((svc) => `${svc.name}${svc.quantity > 1 ? ` x${svc.quantity}` : ""}`)
                    .join(", ")}
                </Typography>
              )}

              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("reservations_details.status")}:
                </Typography>
                <Chip
                  size="small"
                  label={getReservationStatusLabel(selectedReservation.status)}
                  sx={getReservationStatusChipSx(selectedReservation.status)}
                />
              </Box>

              {selectedReservation.status === "TO_CONFIRM" && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t("reservations_details.edit_pending_hint")}
                </Alert>
              )}
              {isSelectedReservationCompleted && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t("reservations_details.completed_locked", { defaultValue: "Le prenotazioni completate non possono essere modificate." })}
                </Alert>
              )}

              <TextField
                select
                fullWidth
                label={t("reservations_details.change_status")}
                value={reservationStatusDraft}
                disabled={savingReservationStatus || isSelectedReservationCompleted}
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  setReservationStatusDraft(nextStatus);
                  if (nextStatus !== "CANCELLED") {
                    setReservationCancellationReason("");
                    setReservationCancellationReasonError("");
                  } else if (reservationCancellationReasonError) {
                    setReservationCancellationReasonError("");
                  }
                }}
              >
                {RESERVATION_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {getReservationStatusLabel(status)}
                  </MenuItem>
                ))}
              </TextField>

              {reservationStatusDraft === "CANCELLED" && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t("reservations_details.cancellation_reason")}
                  placeholder={t("reservations_details.cancellation_reason_placeholder")}
                  value={reservationCancellationReason}
                  onChange={(e) => {
                    setReservationCancellationReason(e.target.value);
                    if (reservationCancellationReasonError) setReservationCancellationReasonError("");
                  }}
                  error={Boolean(reservationCancellationReasonError)}
                  helperText={reservationCancellationReasonError}
                />
              )}

              {selectedReservation.status === "CANCELLED" && !!selectedReservation.cancellationReason && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t("reservations_details.cancellation_reason")}:</strong> {selectedReservation.cancellationReason}
                </Typography>
              )}

              {selectedReservation.status === "TO_CONFIRM" && isEditingPendingReservation && (
                <Paper variant="outlined" sx={{ mt: 1.5, p: 2, borderRadius: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label={getTenantTranslation(t, tenantType, "bookingGuests")}
                      type="number"
                      value={reservationEditData.guests}
                      onChange={(e) => handleReservationEditField("guests", e.target.value)}
                      inputProps={{ min: 1 }}
                    />

                    <Autocomplete
                      multiple
                      options={availableEditTables}
                      value={selectedReservationEditTables}
                      getOptionLabel={(option) =>
                        `${formatTableLabel(option)} (${option.maxSeats} ${getTenantTranslation(t, tenantType, "seats")})`
                        + `${editUnavailableTableIds.has(Number(option.id)) ? " - PRENOTATO" : ""}`
                      }
                      getOptionDisabled={(option) => editUnavailableTableIds.has(Number(option.id))}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      filterSelectedOptions
                      onChange={handleReservationEditTablesChange}
                      renderInput={(params) => (
                        <TextField {...params} label={getTenantTranslation(t, tenantType, "bookingTable")} />
                      )}
                    />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <DateTimeLocal24Field
                          label={t("fromTime")}
                          value={reservationEditData.startTime}
                          onChange={(value) => handleReservationEditField("startTime", value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DateTimeLocal24Field
                          label={t("toTime")}
                          value={reservationEditData.endTime}
                          onChange={(value) => handleReservationEditField("endTime", value)}
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label={t("booking.notes")}
                      value={reservationEditData.note}
                      onChange={(e) => handleReservationEditField("note", e.target.value)}
                    />

                    {reservationEditError && <Alert severity="error">{reservationEditError}</Alert>}
                  </Stack>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReservationModal} disabled={savingReservationStatus || savingReservationEdit}>
            {t("close")}
          </Button>
          {selectedReservation?.status === "TO_CONFIRM" && !isEditingPendingReservation && (
            <Button
              variant="outlined"
              onClick={() => setIsEditingPendingReservation(true)}
              disabled={savingReservationStatus || savingReservationEdit}
            >
              {t("reservations_details.edit_booking")}
            </Button>
          )}
          {selectedReservation?.status === "TO_CONFIRM" && isEditingPendingReservation && (
            <Button
              variant="contained"
              onClick={handleSaveReservationEdits}
              disabled={savingReservationEdit || savingReservationStatus}
            >
              {t("reservations_details.save_booking")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSaveReservationStatus}
            disabled={savingReservationStatus || savingReservationEdit || !selectedReservation || isSelectedReservationCompleted}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={helitePickerOpen}
        onClose={() => { setHelitePickerOpen(false); setHeliteSearch(""); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("heliteGuestPicker.title")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
            value={heliteSearch}
            onChange={(e) => setHeliteSearch(e.target.value)}
            placeholder={t("heliteGuestPicker.searchPlaceholder")}
          />
          {heliteGuestsLoading ? (
            <Typography color="text.secondary">{t("heliteGuestPicker.loadingShort")}</Typography>
          ) : filteredHeliteGuests.length === 0 ? (
            <Typography color="text.secondary">{t("heliteGuestPicker.empty")}</Typography>
          ) : (
            <Box sx={{ maxHeight: 420, overflow: "auto" }}>
              {filteredHeliteGuests.map((guest) => (
                <Box
                  key={`${guest.id}-${guest.idrispren || guest.number || guest.roomnr}`}
                  onClick={() => {
                    setSelectedHeliteGuest(guest);
                    setHelitePickerOpen(false);
                    setHeliteSearch("");
                  }}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography fontWeight={500}>
                    {t("heliteGuestPicker.roomWithNumber", { room: guest.roomnr || "-" })} - {guest.holderName || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("heliteGuestPicker.bookingShort", { number: guest.number || "-" })} | {formatDateDdMmYyyy(guest.arrival)} - {formatDateDdMmYyyy(guest.departure)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHelitePickerOpen(false); setHeliteSearch(""); }}>
            {t("confirmOperation.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

    </SidebarLayout>
  );
};

const Prenotazioni = () => {
  const tenantType = localStorage.getItem("tenantType");
  if (String(tenantType || "").toUpperCase() === "SALE_CONGRESSI") {
    return <CongressReservationsPage />;
  }
  return <RestaurantPrenotazioni />;
};

export default Prenotazioni;





